/**
 * Percent-decoding that answers instead of throwing.
 *
 * `decodeURIComponent` throws a URIError on an escape that is not UTF-8: a
 * lone `%`, a non-hex escape, or a well-formed escape of bytes that are not
 * UTF-8, such as the Latin-1 `%E9` older systems write for é. A URL carrying
 * one parses perfectly happily (`new URL()` does not decode), so the throw
 * surfaces wherever the decode happens rather than where the URL arrived.
 *
 * WHERE THAT BITES. A dynamic route's params are decoded by Next itself
 * (getRouteMatcher), before any page code runs, and on a URIError Next answers
 * with its bare pages-router "400: Bad Request" page: no site chrome, no way
 * back, and none of the middleware's response headers (the CSP among them),
 * because Next drops them on that path. A link such as /tag/caf%E9 reaches the
 * app in production and got exactly that. So the middleware sends any path
 * that fails isDecodablePath to the site's own 404 instead.
 *
 * WHAT NO APP CODE CAN FIX. A path that is not even well-formed percent-encoding
 * (/tag/%ZZ, a trailing `%`) is answered 502 by the App Hosting edge and never
 * forwarded: on 2026-10-16 those requests left no Cloud Run request log at all,
 * and the same paths sent straight to the Cloud Run URL came back from Next as
 * 400 or 404. Only Firebase can change that.
 *
 * path-decoding.test.ts has the cases, pinned against Next's own matcher.
 */

/** `raw` percent-decoded, or null when it cannot be decoded. */
export function safeDecodeURIComponent(raw: string): string | null {
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

/**
 * Whether a URL path can be percent-decoded, which is what Next's route
 * matcher needs of a dynamic route's params.
 *
 * Decoding the whole path is the same test as decoding each segment: `/` is
 * ASCII, so no escape sequence can be valid on one side of it and not the other.
 */
export function isDecodablePath(pathname: string): boolean {
  return safeDecodeURIComponent(pathname) !== null;
}
